import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Upload, 
  Eye,
  EyeOff,
  Heart,
  MessageCircle,
  Calendar,
  Loader,
  Image as ImageIcon,
  Tag,
  FileText,
  ChevronDown
} from 'lucide-react';
import './ArtistBlog.css';

const API_URL = 'https://my-musing-backend.onrender.com/api';

const ArtistBlog = ({ blogs, fetchData, viewport }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogsPage, setBlogsPage] = useState(1);
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  
  const [blogForm, setBlogForm] = useState({
    title: '',
    content: '',
    tags: [],
    images: [], // New images to upload (File objects)
    existingImages: [], // Already uploaded images from database
    published: true
  });
  const [newTag, setNewTag] = useState('');
  const [imagesPreviews, setImagesPreviews] = useState([]); // Combined previews

  const itemsPerPage = useMemo(() => ({
    mobile: 4,
    tablet: 6,
    desktop: 8
  }), []);

  const getItemsPerPage = useCallback(() => {
    if (viewport.isMobile) return itemsPerPage.mobile;
    if (viewport.isTablet) return itemsPerPage.tablet;
    return itemsPerPage.desktop;
  }, [viewport.isMobile, viewport.isTablet, itemsPerPage]);

  const displayedBlogs = useMemo(() => {
    const itemsCount = blogsPage * getItemsPerPage();
    return blogs.slice(0, itemsCount);
  }, [blogs, blogsPage, getItemsPerPage]);

  const hasMoreBlogs = useMemo(() => 
    displayedBlogs.length < blogs.length,
    [displayedBlogs.length, blogs.length]
  );

  const handleShowMore = useCallback(() => {
    setShowMoreLoading(true);
    setTimeout(() => {
      setBlogsPage(prev => prev + 1);
      setShowMoreLoading(false);
      
      setTimeout(() => {
        const currentItems = blogsPage * getItemsPerPage();
        const blogCards = document.querySelectorAll('.blog-card-dashboard');
        if (blogCards[currentItems]) {
          blogCards[currentItems].scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest' 
          });
        }
      }, 100);
    }, 500);
  }, [blogsPage, getItemsPerPage]);

  const resetForm = () => {
    setBlogForm({
      title: '',
      content: '',
      tags: [],
      images: [],
      existingImages: [],
      published: true
    });
    setImagesPreviews([]);
    setEditingBlog(null);
    setPreviewMode(false);
  };

  // Handle multiple image selection
  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Add new files to existing images array
      setBlogForm(prev => ({
        ...prev,
        images: [...prev.images, ...files]
      }));
      
      // Create previews for all newly selected images
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagesPreviews(prev => [...prev, {
            url: reader.result,
            isNew: true,
            file: file
          }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // Remove specific image (handles both existing and new images)
  const handleRemoveImage = (index) => {
    const imageToRemove = imagesPreviews[index];
    
    // Remove from previews
    setImagesPreviews(prev => prev.filter((_, i) => i !== index));
    
    // If it's a new image, remove from images array
    if (imageToRemove.isNew) {
      setBlogForm(prev => ({
        ...prev,
        images: prev.images.filter(img => {
          // Compare file objects by creating a unique identifier
          const fileReader = new FileReader();
          return true; // We'll filter by index below
        })
      }));
      
      // Better approach: track indices
      const newImageIndex = imagesPreviews
        .slice(0, index)
        .filter(img => img.isNew).length;
      
      setBlogForm(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== newImageIndex)
      }));
    } else {
      // If it's an existing image, remove from existingImages array
      const existingImageIndex = imagesPreviews
        .slice(0, index)
        .filter(img => !img.isNew).length;
      
      setBlogForm(prev => ({
        ...prev,
        existingImages: prev.existingImages.filter((_, i) => i !== existingImageIndex)
      }));
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !blogForm.tags.includes(newTag.trim())) {
      setBlogForm({ ...blogForm, tags: [...blogForm.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setBlogForm({ 
      ...blogForm, 
      tags: blogForm.tags.filter(tag => tag !== tagToRemove) 
    });
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setBlogForm({
      title: blog.title,
      content: blog.content,
      tags: blog.tags || [],
      images: [], // Clear new images
      existingImages: blog.images || [], // Preserve existing images
      published: blog.published
    });
    
    // Set existing images previews
    if (blog.images && blog.images.length > 0) {
      setImagesPreviews(blog.images.map(img => ({
        url: img.url,
        publicId: img.publicId,
        isNew: false
      })));
    } else {
      setImagesPreviews([]);
    }
    
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;

    try {
      await axios.delete(`${API_URL}/blogs/${id}`);
      alert('Blog deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Error deleting blog');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!blogForm.title.trim() || !blogForm.content.trim()) {
      alert('Title and content are required');
      return;
    }

    try {
      // Convert only NEW images to base64
      const imagesBase64 = await Promise.all(
        blogForm.images.map(image => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(image);
          });
        })
      );

      const payload = {
        title: blogForm.title,
        content: blogForm.content,
        tags: blogForm.tags,
        imagesBase64: imagesBase64, // Only new images
        existingImages: blogForm.existingImages, // Preserve existing images
        published: blogForm.published
      };

      if (editingBlog) {
        await axios.put(`${API_URL}/blogs/${editingBlog._id}`, payload);
        alert('Blog updated successfully!');
      } else {
        await axios.post(`${API_URL}/blogs`, payload);
        alert('Blog published successfully!');
      }
      
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Error saving blog: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getExcerpt = (content, length = 150) => {
    const plainText = content.replace(/<[^>]*>/g, '');
    return plainText.length > length 
      ? plainText.substring(0, length) + '...' 
      : plainText;
  };

  return (
    <div className="tab-content">
      <div className="tab-header">
        <div>
          <h2>Manage Blog Posts</h2>
          <p>Create and manage your blog content ({displayedBlogs.length} of {blogs.length})</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(true)}
        >
          <Plus size={20} />
          Write New Post
        </button>
      </div>

      {blogs.length === 0 ? (
        <div className="empty-state">
          <FileText size={64} />
          <h3>No blog posts yet</h3>
          <p>Start sharing your creative journey by writing your first blog post</p>
          <button 
            className="btn-primary"
            onClick={() => setShowForm(true)}
          >
            <Plus size={20} />
            Write Your First Post
          </button>
        </div>
      ) : (
        <>
          <div className="blogs-grid-dashboard">
            {displayedBlogs.map((blog) => (
              <div 
                key={blog._id} 
                className="blog-card-dashboard"
              >
                {blog.images && blog.images.length > 0 && (
                  <div className="blog-card-image-dashboard">
                    <img src={blog.images[0].url} alt={blog.title} />
                    <div className="blog-status-badge">
                      <span className={blog.published ? 'status-published' : 'status-draft'}>
                        {blog.published ? (
                          <>
                            <Eye size={12} />
                            Published
                          </>
                        ) : (
                          <>
                            <EyeOff size={12} />
                            Draft
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="blog-card-body-dashboard">
                  <div className="blog-card-meta-dashboard">
                    <div className="meta-item-dashboard">
                      <Calendar size={14} />
                      <span>{formatDate(blog.createdAt)}</span>
                    </div>
                    {!blog.images || blog.images.length === 0 && (
                      <div className="status-badge-inline">
                        <span className={blog.published ? 'status-published' : 'status-draft'}>
                          {blog.published ? (
                            <>
                              <Eye size={12} />
                              Published
                            </>
                          ) : (
                            <>
                              <EyeOff size={12} />
                              Draft
                            </>
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  <h3 className="blog-card-title-dashboard">{blog.title}</h3>
                  <p className="blog-card-excerpt-dashboard">
                    {getExcerpt(blog.content)}
                  </p>

                  {blog.tags && blog.tags.length > 0 && (
                    <div className="blog-card-tags-dashboard">
                      {blog.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="tag-chip-dashboard">{tag}</span>
                      ))}
                      {blog.tags.length > 3 && (
                        <span className="tag-chip-dashboard more">
                          +{blog.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="blog-card-stats-dashboard">
                    <div className="stat-item-dashboard">
                      <Heart size={16} />
                      <span>{blog.likes || 0}</span>
                    </div>
                    <div className="stat-item-dashboard">
                      <MessageCircle size={16} />
                      <span>{blog.comments?.length || 0}</span>
                    </div>
                    {blog.images && blog.images.length > 0 && (
                      <div className="stat-item-dashboard">
                        <ImageIcon size={16} />
                        <span>{blog.images.length}</span>
                      </div>
                    )}
                  </div>

                  <div className="blog-card-actions-dashboard">
                    <button
                      className="btn-secondary01"
                      onClick={() => handleEdit(blog)}
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                    <button
                      className="btn-danger01"
                      onClick={() => handleDelete(blog._id)}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMoreBlogs && (
            <div className="show-more-container">
              <button 
                className="show-more-btn"
                onClick={handleShowMore}
                disabled={showMoreLoading}
                type="button"
              >
                {showMoreLoading ? (
                  <>
                    <Loader className="spinner" size={20} />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <ChevronDown size={20} />
                    <span>Load More ({Math.min(getItemsPerPage(), blogs.length - displayedBlogs.length)} posts)</span>
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Blog Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content blog-modal" onClick={e => e.stopPropagation()}>
            <button 
              className="modal-close" 
              onClick={() => setShowForm(false)}
              type="button"
            >
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <h3>{editingBlog ? 'Edit Blog Post' : 'Write New Blog Post'}</h3>
              <p>Share your creative journey and insights</p>
            </div>
            
            <div className="blog-form-tabs">
              <button
                type="button"
                className={`tab-btn ${!previewMode ? 'active' : ''}`}
                onClick={() => setPreviewMode(false)}
              >
                <Edit size={16} />
                <span>Edit</span>
              </button>
              <button
                type="button"
                className={`tab-btn ${previewMode ? 'active' : ''}`}
                onClick={() => setPreviewMode(true)}
              >
                <Eye size={16} />
                <span>Preview</span>
              </button>
            </div>

            {previewMode ? (
              <div className="blog-preview-container">
                <div className="blog-preview">
                  {imagesPreviews.length > 0 && (
                    <div className={`preview-images-grid images-count-${Math.min(imagesPreviews.length, 4)}`}>
                      {imagesPreviews.map((preview, idx) => (
                        <div key={idx} className="preview-image-item">
                          <img src={preview.url} alt={`Preview ${idx + 1}`} />
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="preview-content">
                    <h1>{blogForm.title || 'Untitled Post'}</h1>
                    {blogForm.tags.length > 0 && (
                      <div className="preview-tags">
                        {blogForm.tags.map(tag => (
                          <span key={tag} className="preview-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    <div 
                      className="preview-body"
                      dangerouslySetInnerHTML={{ 
                        __html: blogForm.content || '<p>Start writing your content...</p>' 
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="modal-form blog-form">
                <div className="form-group">
                  <label>Blog Images</label>
                  <div className="cover-image-upload">
                    {imagesPreviews.length > 0 && (
                      <div className="multiple-images-preview">
                        {imagesPreviews.map((preview, index) => (
                          <div key={index} className="image-preview-item">
                            <img src={preview.url} alt={`Preview ${index + 1}`} />
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={() => handleRemoveImage(index)}
                              title="Remove image"
                            >
                              <X size={16} />
                            </button>
                            {!preview.isNew && (
                              <span className="existing-badge">Saved</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <label className="upload-label">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImagesChange}
                        style={{ display: 'none' }}
                      />
                      <div className="upload-placeholder">
                        <ImageIcon size={48} />
                        <p>Click to upload images</p>
                        <small>Select multiple images for your blog post</small>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    required
                    value={blogForm.title}
                    className="text-input"
                    onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                    placeholder="Enter your blog post title..."
                  />
                </div>

                <div className="form-group">
                  <label>Content *</label>
                  <textarea
                    required
                    rows="15"
                    value={blogForm.content}
                    className="text-input blog-content-textarea"
                    onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                    placeholder="Write your blog content here... You can use HTML tags for formatting."
                  />
                  <small className="form-hint">
                    💡 Tip: Use HTML tags like &lt;p&gt;, &lt;h2&gt;, &lt;strong&gt;, &lt;em&gt; for formatting
                  </small>
                </div>

                <div className="form-group">
                  <label>Tags</label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {blogForm.tags.map(tag => (
                        <span key={tag} className="tag-item">
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="tag-remove"
                          >
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        value={newTag}
                        className="text-input"
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTag();
                          }
                        }}
                        placeholder="Add a tag..."
                      />
                      <button
                        type="button"
                        onClick={handleAddTag}
                        className="btn-secondary"
                      >
                        <Tag size={16} />
                        Add Tag
                      </button>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={blogForm.published}
                      onChange={(e) => setBlogForm({ ...blogForm, published: e.target.checked })}
                    />
                    <span>Publish immediately</span>
                    <small>(Uncheck to save as draft)</small>
                  </label>
                </div>

                <div className="modal-actions">
                  <button type="submit" className="btn-primary">
                    <Save size={20} />
                    {editingBlog ? 'Update Post' : 'Publish Post'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="btn-secondary"
                  >
                    <X size={20} />
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistBlog;